import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { PrismComboSelectInput } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import type CustomTagDefinition from 'lumen/models/custom-tag-definition';
import type CustomTagListOption from 'lumen/models/custom-tag-list-option';
import type CustomTagValue from 'lumen/models/custom-tag-value';

interface Option {
  id: string;
  label: string;
  option: CustomTagListOption;
}

interface Signature {
  Args: {
    cellData: {
      definition: CustomTagDefinition;
      isMixed?: boolean;
      onUpdate: (newValues: CustomTagListOption[]) => void;
      values: CustomTagValue[];
    };
    row: {
      isDisabled?: boolean;
      isReadOnly: boolean;
    };
  };
}

export default class SettingsCustomPropertyTagsCellsTdMultiselectListOption extends Component<Signature> {
  @service declare intl: Services['intl'];

  @cached
  get dropdownOptions(): Option[] {
    return this.args.cellData.definition.options.map((opt) => ({
      id: opt.id ?? '',
      label: opt.label,
      option: opt,
    }));
  }

  get readOnlyLabel(): string {
    const labels = this.selectedOptions.map((o) => o.label);
    return labels.length > 0
      ? labels.join(', ')
      : this.intl.t('general.missing');
  }

  get selectedOptions(): Option[] {
    return this.args.cellData.values
      .map((v) => v.listOption)
      .filter((opt) => opt != null)
      .map((opt) => this.dropdownOptions.find((o) => o.option === opt))
      .filter((opt) => opt !== undefined);
  }

  @action
  buildLabelWithCount(count: number): string {
    if (count === 0 && this.args.cellData.isMixed) {
      return this.intl.t('general.mixed');
    }
    if (count === 1) {
      return this.selectedOptions[0]?.label ?? '';
    }
    return this.intl.t('general.values_selected', { count });
  }

  @action
  handleChange(selected: Option[] | null): void {
    this.args.cellData.onUpdate((selected ?? []).map((opt) => opt.option));
  }

  <template>
    {{#if @row.isReadOnly}}
      <span>{{this.readOnlyLabel}}</span>
    {{else}}
      <div class="flex flex-col">
        <PrismComboSelectInput
          @buildLabelWithCount={{this.buildLabelWithCount}}
          @canUnselectAll={{true}}
          @disabled={{@row.isDisabled}}
          @isSmall={{true}}
          @onChange={{this.handleChange}}
          @options={{this.dropdownOptions}}
          @placeholder={{if @cellData.isMixed (t "general.mixed")}}
          @value={{this.selectedOptions}}
          data-test-id="td-multiselect-list-option"
        />
      </div>
    {{/if}}
  </template>
}
